import type {
  AiSpreadsheetCellKind,
  IAiSpreadsheetPasteEdit,
  IManualRowInput,
} from "@/payroll/ai-script/types";
import type { WorkbookHistoryOp } from "./workbook-history-types";

interface ReplayCellResult {
  version?: number;
}

export interface ReplayOpMutations {
  updateCell: (params: {
    sheetId: string;
    cellId: string;
    rawInput: string;
    kind?: AiSpreadsheetCellKind;
    valueType?: string;
    baseVersion?: number;
  }) => Promise<ReplayCellResult>;
  bulkPaste: (params: {
    sheetId: string;
    edits: IAiSpreadsheetPasteEdit[];
    baseVersion?: number;
  }) => Promise<ReplayCellResult>;
  createManualRow: (params: {
    sheetId: string;
    rows: IManualRowInput[];
    baseVersion?: number;
  }) => Promise<ReplayCellResult>;
  deleteManualRow: (params: {
    sheetId: string;
    manualRowId: string;
    baseVersion?: number;
  }) => Promise<ReplayCellResult>;
  restoreManualRow: (params: {
    sheetId: string;
    manualRowId: string;
    baseVersion?: number;
  }) => Promise<ReplayCellResult>;
  bulkDeleteRows: (params: {
    sheetId: string;
    manualRowIds: string[];
    baseVersion?: number;
  }) => Promise<ReplayCellResult>;
  bulkRestoreRows: (params: {
    sheetId: string;
    manualRowIds: string[];
    baseVersion?: number;
  }) => Promise<ReplayCellResult>;
  resolveCellRawInput: (sheetId: string, cellId: string) => string;
}

export const replayOp = async (
  op: WorkbookHistoryOp,
  deps: ReplayOpMutations,
  baseVersion?: number,
): Promise<number | undefined> => {
  switch (op.type) {
    case "cellEdit": {
      const result = await deps.updateCell({
        sheetId: op.sheetId,
        cellId: op.cellId,
        rawInput: op.rawInput,
        kind: op.kind,
        valueType: op.valueType,
        baseVersion,
      });
      return result.version;
    }
    case "bulkCells": {
      const result = await deps.bulkPaste({
        sheetId: op.sheetId,
        edits: op.edits,
        baseVersion,
      });
      return result.version;
    }
    case "formatEdit": {
      const result = await deps.updateCell({
        sheetId: op.sheetId,
        cellId: op.cellId,
        rawInput: deps.resolveCellRawInput(op.sheetId, op.cellId),
        valueType: op.valueType,
        baseVersion,
      });
      return result.version;
    }
    case "manualRowCreate": {
      const result = await deps.createManualRow({
        sheetId: op.sheetId,
        rows: op.rows,
        baseVersion,
      });
      return result.version;
    }
    case "manualRowDelete": {
      const result = await deps.deleteManualRow({
        sheetId: op.sheetId,
        manualRowId: op.manualRowId,
        baseVersion,
      });
      return result.version;
    }
    case "manualRowRestore": {
      const result = await deps.restoreManualRow({
        sheetId: op.sheetId,
        manualRowId: op.manualRowId,
        baseVersion,
      });
      return result.version;
    }
    case "bulkRowsDelete": {
      const result = await deps.bulkDeleteRows({
        sheetId: op.sheetId,
        manualRowIds: op.manualRowIds,
        baseVersion,
      });
      return result.version;
    }
    case "bulkRowsRestore": {
      const result = await deps.bulkRestoreRows({
        sheetId: op.sheetId,
        manualRowIds: op.manualRowIds,
        baseVersion,
      });
      return result.version;
    }
  }
};

export const replayOps = async (
  ops: WorkbookHistoryOp[],
  deps: ReplayOpMutations,
  baseVersion?: number,
): Promise<number | undefined> => {
  let version = baseVersion;

  for (const op of ops) {
    const next = await replayOp(op, deps, version);
    if (next != null) version = next;
  }

  return version;
};
